/**
 * Common utilities for the P2P transfer pages (sender and receiver).
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  createSignalingClient,
  type SignalingClient,
  SignalingError,
  type SnapshotDb,
  type TransferErrorKind,
  TransferProtocolError,
} from "@stockpilot/shared";
import { SERVER_BASE_URL } from "@/lib/api";

/**
 * Signaling client for this build's backend. The `/api/p2p/*`
 * endpoints are public (the receiver may be a device without an account),
 * so the platform fetch without credentials is enough.
 */
export function createP2pSignalingClient(): SignalingClient {
  return createSignalingClient({ baseUrl: SERVER_BASE_URL.replace(/\/$/, "") });
}

/** Collapses any flow failure into a `TransferErrorKind` for the UI. */
export function mapTransferError(error: unknown): TransferErrorKind {
  if (error instanceof SignalingError) {
    return error.kind;
  }
  if (error instanceof TransferProtocolError) {
    return error.reason === "too-large" ? "too-large" : "transfer-failed";
  }
  if (error instanceof Error) {
    if (error.message === "p2p-connect-timeout" || error.message === "p2p-connection-failed") {
      return "connection-failed";
    }
    if (error.message === "p2p-signaling-failed") {
      return "network";
    }
  }
  return "transfer-failed";
}

/**
 * Structural PowerSync → `SnapshotDb` bridge (the minimum surface used by
 * buildSnapshot/importSnapshot) without type assertions.
 */
export function toSnapshotDb(db: AbstractPowerSyncDatabase): SnapshotDb {
  return {
    getAll: async (sql, params) => db.getAll(sql, params),
    execute: async (sql, params) => db.execute(sql, params),
    writeTransaction: async (fn) =>
      db.writeTransaction(async (tx) =>
        fn({
          getAll: async (sql, params) => tx.getAll(sql, params),
          execute: async (sql, params) => tx.execute(sql, params),
        }),
      ),
  };
}
